"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import {
	MAGIC_CARDS_DECK_TRANSFORM_MS,
	MAGIC_CARDS_DECK_TRANSFORM_MS_REDUCED,
} from "./magic-cards";

function waitMs(ms: number): Promise<void> {
	return new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});
}

/** Deck morph duration matching `.magic-card-transform-layer` CSS. */
export function magicCardsDeckTransformMs(reducedMotion: boolean): number {
	return reducedMotion
		? MAGIC_CARDS_DECK_TRANSFORM_MS_REDUCED
		: MAGIC_CARDS_DECK_TRANSFORM_MS;
}

export interface MagicCardsDeckShuffleControls {
	/**
	 * Pass through to {@link MagicCards}:
	 * `deckCollapseKey`, `fanExpandKey`, `focusResetKey`.
	 * Fan-out matches hub Switch: bump `fanExpandKey` **and** sync
	 * `focusResetKey={String(fanExpandKey)}` (focus reset is what fans the deck).
	 */
	deckCollapseKey: number;
	fanExpandKey: number;
	focusResetKey: string;
	/** Collapse fan → stacked deck (same morph as picking a card). */
	collapseDeck: () => void;
	/** Fan stacked deck back out (same morph as hub Switch). */
	expandDeck: () => void;
	/**
	 * Collapse → wait morph → `work` (swap data) → expand → wait morph.
	 * Rejects only if `work` throws after expand is still attempted.
	 */
	runShuffleRitual: (work: () => void | Promise<void>) => Promise<void>;
	/** True while {@link runShuffleRitual} is in flight. */
	shuffleBusy: boolean;
}

/**
 * Shared fan↔deck ritual controls used by hub Switch and battle boost reroll.
 * Wire the returned keys onto {@link MagicCards}; call {@link MagicCardsDeckShuffleControls.runShuffleRitual}
 * (or collapse/expand) instead of inventing a second animation path.
 */
export function useMagicCardsDeckShuffleControls(params: {
	reducedMotion: boolean;
}): MagicCardsDeckShuffleControls {
	const transformMs = magicCardsDeckTransformMs(params.reducedMotion);
	const [deckCollapseKey, setDeckCollapseKey] = useState(0);
	const [fanExpandKey, setFanExpandKey] = useState(0);
	const [shuffleBusy, setShuffleBusy] = useState(false);
	const inFlightRef = useRef(false);

	const focusResetKey = useMemo(
		() => String(fanExpandKey),
		[fanExpandKey],
	);

	const collapseDeck = useCallback(() => {
		setDeckCollapseKey((k) => k + 1);
	}, []);

	const expandDeck = useCallback(() => {
		setFanExpandKey((k) => k + 1);
	}, []);

	const runShuffleRitual = useCallback(
		async (work: () => void | Promise<void>) => {
			if (inFlightRef.current) {
				return;
			}
			inFlightRef.current = true;
			setShuffleBusy(true);
			try {
				collapseDeck();
				await waitMs(transformMs);
				try {
					await Promise.resolve(work());
				} catch (error) {
					expandDeck();
					await waitMs(transformMs);
					throw error;
				}
				// Let React commit swapped cards while still collapsed.
				await waitMs(0);
				expandDeck();
				await waitMs(transformMs);
			} finally {
				inFlightRef.current = false;
				setShuffleBusy(false);
			}
		},
		[collapseDeck, expandDeck, transformMs],
	);

	return {
		deckCollapseKey,
		fanExpandKey,
		focusResetKey,
		collapseDeck,
		expandDeck,
		runShuffleRitual,
		shuffleBusy,
	};
}
